import { Link, useParams } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { ArrowLeft, ArrowRight, GraduationCap, Clock } from "lucide-react";

const CLASSES = [
  { id: "IX", label: "Class IX", grade: "9th", desc: "Foundation year" },
  { id: "X", label: "Class X", grade: "10th", desc: "Board exam prep" },
  { id: "XI", label: "Class XI", grade: "11th", desc: "ISC introduction" },
  { id: "XII", label: "Class XII", grade: "12th", desc: "Final boards" },
];

const SUBJECT_META = {
  computer: { name: "Computer", color: "hsl(200 90% 50%)", classes: ["IX", "X", "XI", "XII"] },
  english: { name: "English", color: "hsl(10 85% 62%)", classes: ["IX", "X", "XI", "XII"] },
  mathematics: { name: "Mathematics", color: "hsl(260 70% 60%)", classes: ["IX", "X", "XI", "XII"] },
};

export default function ClassesPage() {
  const { subjectId } = useParams();
  const subject = SUBJECT_META[subjectId] || SUBJECT_META.computer;
  const isAvailable = (id) => subject.classes.includes(id);

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <Link
          to="/subjects"
          data-testid="back-to-subjects"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to subjects
        </Link>

        <div className="mb-10">
          <div
            className="text-sm font-bold uppercase tracking-widest mb-3"
            style={{ color: subject.color }}
          >
            {subject.name}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">
            Pick your class
          </h1>
          <p className="text-lg text-muted-foreground">
            Choose your current class to see the chapters.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {CLASSES.map((c) => {
            const available = isAvailable(c.id);
            const Wrapper = available ? Link : "div";
            const wrapperProps = available
              ? {
                  to: `/subjects/${subjectId}/classes/${c.id}/topics`,
                  "data-testid": `class-card-${c.id}`,
                }
              : {
                  "data-testid": `class-card-${c.id}-coming-soon`,
                  "aria-disabled": true,
                };
            return (
              <Wrapper
                key={c.id}
                {...wrapperProps}
                className={`group tactile-card p-7 relative overflow-hidden ${
                  available ? "" : "opacity-60 cursor-not-allowed"
                }`}
              >
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl opacity-10 transition-opacity group-hover:opacity-25"
                  style={{ background: subject.color }}
                />
                <div className="relative">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                    style={{
                      background: `${subject.color}15`,
                      color: subject.color,
                    }}
                  >
                    <GraduationCap className="w-6 h-6" />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                    {c.grade} Standard
                  </div>
                  <div className="text-3xl font-black mt-1 mb-1">{c.label}</div>
                  <div className="text-sm text-muted-foreground mb-6">
                    {c.desc}
                  </div>
                  {available ? (
                    <div
                      className="inline-flex items-center gap-1.5 text-sm font-bold"
                      style={{ color: subject.color }}
                    >
                      View chapters <ArrowRight className="w-4 h-4" />
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 text-sm font-bold text-muted-foreground">
                      <Clock className="w-4 h-4" /> Coming soon
                    </div>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </main>
    </div>
  );
}
